import { useQuery } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import { Cloud, History, Music, Search as SearchIcon, X } from "lucide-react";
import React, { useEffect, useState } from "react";
import { EmptyState } from "../components/EmptyState";
import { MediaGrid } from "../components/MediaGrid";
import { ProviderTabs } from "../components/ProviderTabs";
import { SearchHistory } from "../components/SearchHistory";
import { useDebounce } from "../hooks/useDebounce";
import { useSpotifyAuth } from "../hooks/useSpotifyAuth";
import { spotifyApi } from "../lib/spotify";
import { useLocalLibraryStore } from "../stores/localLibraryStore";
import { useSearchStore } from "../stores/searchStore";
import { MediaItemProps } from "../types";

export const Search = () => {
  const [query, setQuery] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeProvider, setActiveProvider] = useState<"local" | "spotify">("local");
  const { isAuthenticated, login } = useSpotifyAuth();
  const { getAllAlbums, getAllPlaylists, getAllTracks } = useLocalLibraryStore();
  const { addToHistory, getSuggestions } = useSearchStore();

  const debouncedQuery = useDebounce(query, 300);
  const suggestions = getSuggestions(query);

  const { data: spotifyResults, isLoading } = useQuery({
    queryKey: ["search", debouncedQuery, activeProvider],
    queryFn: () => spotifyApi.search(debouncedQuery, ["track", "album", "artist"]),
    enabled: debouncedQuery.length > 0 && activeProvider === "spotify" && isAuthenticated,
  });

  const localAlbums = getAllAlbums();
  const localPlaylists = getAllPlaylists();
  const localTracks = getAllTracks();

  const localResults = debouncedQuery
    ? {
        tracks: localTracks.filter(
          (track) =>
            track.title.toLowerCase().includes(debouncedQuery.toLowerCase()) ||
            track.artist.toLowerCase().includes(debouncedQuery.toLowerCase())
        ),
        albums: localAlbums.filter(
          (album) =>
            album.name.toLowerCase().includes(debouncedQuery.toLowerCase()) ||
            album.artist.toLowerCase().includes(debouncedQuery.toLowerCase())
        ),
        playlists: localPlaylists.filter((playlist) =>
          playlist.name.toLowerCase().includes(debouncedQuery.toLowerCase())
        ),
      }
    : { tracks: [], albums: [], playlists: [] };

  const localResultsCount = localResults.tracks.length + localResults.albums.length + localResults.playlists.length;

  useEffect(() => {
    if (debouncedQuery && (localResultsCount > 0 || spotifyResults?.tracks.items.length)) {
      addToHistory({
        query: debouncedQuery,
        provider: activeProvider,
        resultCount: activeProvider === "local" ? localResultsCount : spotifyResults?.tracks.items.length || 0,
      });
    }
  }, [debouncedQuery, localResultsCount, spotifyResults?.tracks.items.length]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setShowSuggestions(false);
  };

  const handleSuggestionClick = (suggestion: string) => {
    setQuery(suggestion);
    setShowSuggestions(false);
  };

  const renderContent = () => {
    if (activeProvider === "spotify" && !isAuthenticated) {
      return (
        <EmptyState
          icon={Cloud}
          title="Connect to Spotify"
          description="Connect your Spotify account to search Spotify's library"
          action={{
            label: "Connect Spotify",
            onClick: login,
          }}
        />
      );
    }

    if (!debouncedQuery) {
      return <SearchHistory onSelect={setQuery} />;
    }

    if (activeProvider === "local") {
      if (localResultsCount === 0) {
        return (
          <EmptyState
            icon={Music}
            title="No results found"
            description="Try searching for something else or browse your local files"
            action={{
              label: "Browse Files",
              href: "/local",
            }}
          />
        );
      }

      return (
        <>
          {localResults.tracks.length > 0 && (
            <>
              <h2 className="text-xl font-semibold mb-4">Tracks</h2>
              <MediaGrid
                items={localResults.tracks.map(
                  (track): MediaItemProps => ({
                    id: track.id,
                    name: track.title,
                    artist: track.artist,
                    image: track.albumArt,
                    type: "track",
                    trackCount: 0,
                    source: "local",
                  })
                )}
              />
            </>
          )}
          {localResults.albums.length > 0 && (
            <>
              <h2 className="text-xl font-semibold mt-8 mb-4">Albums</h2>
              <MediaGrid
                items={localResults.albums.map(
                  (album): MediaItemProps => ({
                    id: album.id,
                    name: album.name,
                    artist: album.artist,
                    image: album.coverUrl,
                    type: "album",
                    trackCount: album.tracks.length,
                    source: "local",
                  })
                )}
              />
            </>
          )}
          {localResults.playlists.length > 0 && (
            <>
              <h2 className="text-xl font-semibold mt-8 mb-4">Playlists</h2>
              <MediaGrid
                items={localResults.playlists.map(
                  (playlist): MediaItemProps => ({
                    id: playlist.id,
                    name: playlist.name,
                    artist: "", // Playlists don't have an artist, but it's required by MediaItemProps
                    image: playlist.coverUrl,
                    type: "playlist",
                    trackCount: playlist.tracks.length,
                    source: "local",
                  })
                )}
              />
            </>
          )}
        </>
      );
    }

    if (isLoading) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500" />
        </div>
      );
    }

    if (
      !spotifyResults ||
      (spotifyResults.tracks.items.length === 0 &&
        spotifyResults.albums.items.length === 0 &&
        spotifyResults.artists.items.length === 0)
    ) {
      return (
        <EmptyState
          icon={SearchIcon}
          title="No results found"
          description="Try searching for something else"
          action={{
            label: "Clear Search",
            onClick: () => setQuery(""),
          }}
        />
      );
    }

    return (
      <>
        {spotifyResults.tracks.items.length > 0 && (
          <>
            <h2 className="text-xl font-semibold mb-4">Tracks</h2>
            <MediaGrid
              items={spotifyResults.tracks.items.map(
                (track): MediaItemProps => ({
                  id: track.id,
                  name: track.name,
                  artist: track.artists[0].name,
                  image: track.album.images[0]?.url,
                  type: "track",
                  trackCount: 0,
                  source: "spotify",
                })
              )}
            />
          </>
        )}
        {spotifyResults.albums.items.length > 0 && (
          <>
            <h2 className="text-xl font-semibold mt-8 mb-4">Albums</h2>
            <MediaGrid
              items={spotifyResults.albums.items.map(
                (album): MediaItemProps => ({
                  id: album.id,
                  name: album.name,
                  artist: album.artists[0].name,
                  image: album.images[0]?.url,
                  type: "album",
                  trackCount: album.total_tracks,
                  source: "spotify",
                })
              )}
            />
          </>
        )}
        {spotifyResults.artists.items.length > 0 && (
          <>
            <h2 className="text-xl font-semibold mt-8 mb-4">Artists</h2>
            <MediaGrid
              items={spotifyResults.artists.items.map(
                (artist): MediaItemProps => ({
                  id: artist.id,
                  name: artist.name,
                  artist: artist.name, // For artists, we use the name as both name and artist
                  image: artist.images[0]?.url,
                  type: "artist",
                  trackCount: 0, // Artists don't have a track count in this context
                  source: "spotify",
                })
              )}
            />
          </>
        )}
      </>
    );
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="max-w-2xl mx-auto mb-8">
        <form onSubmit={handleSubmit} className="relative">
          <SearchIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 text-zinc-400" />
          <input
            type="text"
            placeholder={`Search ${activeProvider === "local" ? "your library" : "Spotify"}`}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setShowSuggestions(true);
            }}
            onFocus={() => setShowSuggestions(true)}
            className="w-full pl-12 pr-12 py-3 bg-zinc-800 rounded-full focus:outline-none focus:ring-2 focus:ring-yellow-500 transition-all"
          />
          {query && (
            <button
              type="button"
              onClick={() => {
                setQuery("");
                setShowSuggestions(false);
              }}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 text-zinc-400 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
          )}

          <AnimatePresence>
            {showSuggestions && suggestions.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="absolute w-full mt-2 bg-zinc-800 rounded-lg shadow-lg overflow-hidden z-50"
              >
                {suggestions.map((suggestion, index) => (
                  <button
                    key={suggestion}
                    onClick={() => handleSuggestionClick(suggestion)}
                    className="w-full px-4 py-3 flex items-center gap-3 hover:bg-zinc-700 transition text-left"
                  >
                    <History className="w-4 h-4 text-zinc-400" />
                    <span>{suggestion}</span>
                  </button>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </form>
      </motion.div>

      <ProviderTabs activeProvider={activeProvider} onProviderChange={setActiveProvider} />

      {renderContent()}
    </div>
  );
};
